/**
 * PR-G — Per-entity grip computation for the Three.js renderer.
 *
 * Pure-data layer: takes an `EntityPreview` (and, for INSERT entities,
 * the resolved `BlockDefinition` map) and returns the list of grips
 * the HTML overlay layer must paint. No DOM, no React — so this module
 * is unit-testable in jsdom without mounting any renderer.
 *
 * The output `Grip[]` is a thin reshape of:
 *   - `apps/web/src/app/scene/gripCalculation.ts:calculateGrips()`     (entity-shape grips)
 *   - `apps/web/src/app/scene/blockGripCollector.ts:collectBlockGrips()` (dynamic-block grips)
 *   - `apps/web/src/app/scene/gripCalculation.ts:decodeMleaderRoots()` walked into
 *     arrow / vertex / landing / dogleg / text grips for multileader.
 *
 * Why we don't import the existing helpers verbatim:
 *   - `calculateGrips` returns `GripPoint` (square / triangle / diamond
 *     glyph hints) — the new HTML overlay needs broader glyph kinds
 *     (`lookup-chevron`, `visibility-chevron`, `alignment`).
 *   - The R3F layer needs zero DOM dependencies; the existing helpers
 *     live in a directory that pulls in React types via their callers.
 *
 * Glyph mapping (data-grip-shape attribute):
 *   - "square"             — standard endpoint / midpoint / center / corner.
 *   - "triangle"           — count-adjustment grips, spline tangent grips.
 *   - "diamond"            — spacing-adjustment grips (legacy array spacing).
 *   - "lookup-chevron"     — dynamic-block lookup parameter.
 *   - "visibility-chevron" — dynamic-block visibility parameter.
 *   - "alignment"          — dynamic-block alignment / INSERT origin grip.
 *
 * AutoCAD parity (CLAUDE.md ПРИОРИТЕТ #1):
 *   - LINE        → start, midpoint, end                (3 grips)
 *   - CIRCLE      → center + 4 quadrants                (5 grips)
 *   - ARC         → center, start, mid, end             (4 grips)
 *   - ELLIPSE     → center + 4 axis quadrants           (5 grips)
 *   - TEXT/MTEXT  → insertion (alignment grip)          (1 grip)
 *   - DIMENSION   → ext1, ext2, dim-line                (3 grips)
 *   - MULTILEADER → arrow + vertices + landing + dogleg + text (variable)
 *   - HATCH       → boundary path corners               (≥ 2 grips)
 *   - VIEWPORT    → 2 corner grips                      (2 grips)
 *   - REVCLOUD    → 2 corner grips                      (2 grips)
 *   - INSERT      → insertion + dynamic-parameter grips (1 + Σ params)
 *   - ARRAY-RECT  → origin + col/row count + col/row spacing (5 grips)
 *   - LWPOLYLINE  → vertex grips + segment-midpoint grips (bulge-aware)
 *   - SPLINE      → control-point grips (+ fit-point grips if present)
 *
 * GRIPS-SUBSYSTEM (§5.11): LWPOLYLINE + SPLINE grips are now produced by
 * `gripCalculation.ts` (`buildLwpolylineGrips` / `buildSplineGrips`) and
 * flow through the shared `calculateGrips` path below, so both this R3F
 * renderer and the legacy SVG stage paint them identically.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { BlockDefinition } from "@dxf-ai-editor/drawing-model";

import { calculateGrips } from "../../scene/gripCalculation";
import {
  collectBlockGrips,
  type BlockGrip,
  type BlockGripKind,
} from "../../scene/blockGripCollector";
import {
  decodeMleaderRoots,
  computeDoglegEndpoint,
} from "../../scene/gripCalculation";
import { readNumber } from "../../scene/renderHelpers";

import type { Vec2 } from "../types";

/**
 * Visual glyph the HTML overlay paints for this grip.
 *
 * - "square"             — default 8×8 rect.
 * - "triangle"           — isosceles triangle (apex direction is set
 *                          via `gripRotationDeg` in payload).
 * - "diamond"            — rotated square (45°).
 * - "lookup-chevron"     — small downward-pointing chevron
 *                          (≡ AutoCAD lookup parameter handle).
 * - "visibility-chevron" — small downward-pointing chevron with a
 *                          visibility-state badge.
 * - "alignment"          — alignment / INSERT-base parallelogram glyph.
 */
export type GripShape =
  | "square"
  | "triangle"
  | "diamond"
  | "lookup-chevron"
  | "visibility-chevron"
  | "alignment";

/**
 * Semantic role of the grip — drives which property the drag handler
 * patches (mirrors `GripPoint.gripRole` + `BlockGrip.kind`).
 *
 * The string is opaque to the overlay; consumers (Scene3DStage) compare
 * to one of the documented values to decide which command to dispatch.
 */
export type GripKind =
  // Generic positional grips (entity-shape):
  | "endpoint"
  | "midpoint"
  | "center"
  | "quadrant"
  | "insertion"
  | "corner"
  | "dim-line"
  | "extension-line"
  // Polyline grip kinds (LWPOLYLINE):
  | "polyline-vertex"
  | "polyline-segment-mid"
  // Spline grip kinds:
  | "spline-control-point"
  | "spline-fit-point"
  // Multileader grip kinds:
  | "mleader-arrow"
  | "mleader-vertex"
  | "mleader-landing"
  | "mleader-dogleg"
  | "mleader-text"
  // Array-rect grip kinds:
  | "array-origin"
  | "array-column-count"
  | "array-row-count"
  | "array-column-spacing"
  | "array-row-spacing"
  // Dynamic-block parameter grip kinds (one per BlockGripKind):
  | "block-linear-base"
  | "block-linear-second"
  | "block-flip"
  | "block-visibility"
  | "block-basepoint"
  | "block-point"
  | "block-polar-radial"
  | "block-polar-angle"
  | "block-xy-x"
  | "block-xy-y"
  | "block-rotation"
  | "block-lookup"
  | "block-constraint-horizontal"
  | "block-constraint-vertical"
  | "block-constraint-angular"
  | "block-alignment"
  | "block-constraint-aligned"
  | "block-constraint-radial"
  | "block-constraint-diameter"
  | "block-minsert-columns"
  | "block-minsert-rows"
  | "block-minsert-col-spacing"
  | "block-minsert-row-spacing";

/**
 * One grip slot in the scene. World coords are AutoCAD-world (the
 * coordinate-origin offset is applied by the projection helper at
 * paint time, not here — the overlay holds raw drawing coords so the
 * grip→property dispatch keeps round-tripping with the existing
 * commit handlers).
 */
export interface Grip {
  /**
   * Stable per-entity slot identifier (e.g. "start", "mid", "end").
   * Combined with `entityId` to form a unique testid:
   * `grip-${entityId}-${id}`.
   */
  readonly id: string;
  readonly entityId: string;
  readonly kind: GripKind;
  readonly shape: GripShape;
  readonly world: Vec2;
  /** Raw GripPoint or BlockGrip payload — opaque to the overlay. */
  readonly payload?: unknown;
  /**
   * Glyph rotation hint (degrees). Triangle / alignment grips set this
   * so the apex points along the array's column/row axis (AutoCAD
   * parity for rotated arrays). Square / diamond / chevron grips
   * ignore this value.
   */
  readonly rotationDeg?: number;
}

/**
 * Map a `BlockGripKind` to the GripKind enum + glyph shape used by the
 * overlay. Centralised so Scene3DStage and the overlay don't drift on
 * which kinds map to which icons.
 */
function mapBlockGripKind(
  kind: BlockGripKind,
): { kind: GripKind; shape: GripShape } {
  switch (kind) {
    case "linear-base":
      return { kind: "block-linear-base", shape: "square" };
    case "linear-second":
      return { kind: "block-linear-second", shape: "square" };
    case "flip":
      return { kind: "block-flip", shape: "triangle" };
    case "visibility":
      return { kind: "block-visibility", shape: "visibility-chevron" };
    case "basepoint":
      return { kind: "block-basepoint", shape: "square" };
    case "point":
      return { kind: "block-point", shape: "square" };
    case "polar-radial":
      return { kind: "block-polar-radial", shape: "square" };
    case "polar-angle":
      return { kind: "block-polar-angle", shape: "diamond" };
    case "xy-x":
      return { kind: "block-xy-x", shape: "square" };
    case "xy-y":
      return { kind: "block-xy-y", shape: "square" };
    case "rotation":
      return { kind: "block-rotation", shape: "diamond" };
    case "lookup":
      return { kind: "block-lookup", shape: "lookup-chevron" };
    case "constraint-horizontal":
      return { kind: "block-constraint-horizontal", shape: "square" };
    case "constraint-vertical":
      return { kind: "block-constraint-vertical", shape: "square" };
    case "constraint-angular":
      return { kind: "block-constraint-angular", shape: "diamond" };
    case "alignment":
      return { kind: "block-alignment", shape: "alignment" };
    case "constraint-aligned":
      return { kind: "block-constraint-aligned", shape: "square" };
    case "constraint-radial":
      return { kind: "block-constraint-radial", shape: "square" };
    case "constraint-diameter":
      return { kind: "block-constraint-diameter", shape: "square" };
    case "minsert-columns":
      return { kind: "block-minsert-columns", shape: "triangle" };
    case "minsert-rows":
      return { kind: "block-minsert-rows", shape: "triangle" };
    case "minsert-col-spacing":
      return { kind: "block-minsert-col-spacing", shape: "diamond" };
    case "minsert-row-spacing":
      return { kind: "block-minsert-row-spacing", shape: "diamond" };
    default: {
      const _never: never = kind;
      void _never;
      return { kind: "block-point", shape: "square" };
    }
  }
}

/**
 * Translate an entity-shape `GripPoint.id` to the GripKind enum +
 * default shape. Falls back to the `gripShape` hint when the id is one
 * of the array-specific grip ids.
 */
function entityGripKind(
  entityType: string,
  rawId: string,
  gripShape?: "square" | "triangle" | "diamond",
  gripRole?:
    | "origin"
    | "column-count"
    | "row-count"
    | "column-spacing"
    | "row-spacing"
    | "axis-angle",
): { kind: GripKind; shape: GripShape } {
  // Array-rect kinds first — gripRole is more specific than the id.
  if (gripRole === "origin") return { kind: "array-origin", shape: "square" };
  if (gripRole === "column-count")
    return { kind: "array-column-count", shape: "triangle" };
  if (gripRole === "row-count")
    return { kind: "array-row-count", shape: "triangle" };
  if (gripRole === "column-spacing")
    return { kind: "array-column-spacing", shape: "diamond" };
  if (gripRole === "row-spacing")
    return { kind: "array-row-spacing", shape: "diamond" };

  // Dynamic per-vertex / per-point grip ids (LWPOLYLINE + SPLINE) carry
  // a numeric suffix, so match by prefix before the fixed-id switch.
  if (rawId.startsWith("vertex-"))
    return { kind: "polyline-vertex", shape: "square" };
  if (rawId.startsWith("seg-mid-"))
    return { kind: "polyline-segment-mid", shape: "square" };
  if (rawId.startsWith("cp-"))
    return { kind: "spline-control-point", shape: "square" };
  if (rawId.startsWith("fit-"))
    return { kind: "spline-fit-point", shape: "square" };

  // Standard entity grip ids:
  switch (rawId) {
    case "start":
    case "end":
      // ARC start/end shape is square (matches AutoCAD); LINE same.
      return { kind: "endpoint", shape: gripShape ?? "square" };
    case "midpoint":
    case "arc-midpoint":
      return { kind: "midpoint", shape: gripShape ?? "square" };
    case "center":
      return { kind: "center", shape: gripShape ?? "square" };
    case "quad-right":
    case "quad-top":
    case "quad-left":
    case "quad-bottom":
    case "major-pos":
    case "major-neg":
    case "minor-pos":
    case "minor-neg":
      return { kind: "quadrant", shape: gripShape ?? "square" };
    case "insertion":
      // Block insertion grip rendered as alignment glyph for AutoCAD
      // parity; non-block insertion (TEXT / MTEXT) uses a square.
      return {
        kind: "insertion",
        shape: entityType === "Block" ? "alignment" : "square",
      };
    case "corner1":
    case "corner2":
      return { kind: "corner", shape: gripShape ?? "square" };
    case "ext1":
    case "ext2":
      return { kind: "extension-line", shape: "square" };
    case "dimline":
      return { kind: "dim-line", shape: "square" };
    case "arrow":
      return { kind: "mleader-arrow", shape: "square" };
    case "landing":
      return { kind: "mleader-landing", shape: "square" };
    default:
      return { kind: "endpoint", shape: gripShape ?? "square" };
  }
}

/**
 * Compute the multileader grip set (arrow + vertex + landing + dogleg
 * + text). Mirrors `apps/web/src/app/scene/renderMultileaderGrips.tsx`
 * but emits structured Grip records instead of SVG nodes.
 */
function computeMultileaderGrips(entity: EntityPreview): Grip[] {
  const out: Grip[] = [];
  const roots = decodeMleaderRoots(entity.properties.__mleader_leader_roots);

  const landingEnabled =
    entity.properties.MLENT_LANDING_ENABLED === "true" ||
    entity.properties.MLVAR_LANDING_ENABLED === "true";
  const doglegEnabled =
    entity.properties.MLENT_DOGLEG_ENABLED === "true" ||
    entity.properties.MLVAR_DOGLEG_ENABLED === "true";
  const doglegLength = readNumber(
    entity.properties,
    "MLENT_DOGLEG_LENGTH",
    readNumber(entity.properties, "MLVAR_DOGLEG_LENGTH", 8),
  );
  const textLocationX = readNumber(entity.properties, "Landing X");
  const textLocationY = readNumber(entity.properties, "Landing Y");

  for (let ri = 0; ri < roots.length; ri++) {
    const root = roots[ri];
    if (!root) continue;
    for (let ni = 0; ni < root.n.length; ni++) {
      const node = root.n[ni];
      if (!node) continue;
      for (let li = 0; li < node.l.length; li++) {
        const line = node.l[li];
        if (!line || line.v.length === 0) continue;
        const verts = line.v;

        // Arrow grip — first vertex.
        out.push({
          id: `mleader-arrow-${ri}-${ni}-${li}`,
          entityId: entity.id,
          kind: "mleader-arrow",
          shape: "square",
          world: { x: verts[0][0], y: verts[0][1] },
          payload: {
            kind: "arrow",
            rootIndex: ri,
            nodeIndex: ni,
            lineIndex: li,
            vertexIndex: 0,
          },
        });

        // Intermediate vertex grips.
        for (let vi = 1; vi < verts.length - 1; vi++) {
          out.push({
            id: `mleader-vertex-${ri}-${ni}-${li}-${vi}`,
            entityId: entity.id,
            kind: "mleader-vertex",
            shape: "square",
            world: { x: verts[vi][0], y: verts[vi][1] },
            payload: {
              kind: "vertex",
              rootIndex: ri,
              nodeIndex: ni,
              lineIndex: li,
              vertexIndex: vi,
            },
          });
        }

        // Landing grip — last vertex (only when more than one vertex).
        if (verts.length > 1) {
          const lastIdx = verts.length - 1;
          out.push({
            id: `mleader-landing-${ri}-${ni}-${li}`,
            entityId: entity.id,
            kind: "mleader-landing",
            shape: "square",
            world: { x: verts[lastIdx][0], y: verts[lastIdx][1] },
            payload: {
              kind: "landing",
              rootIndex: ri,
              nodeIndex: ni,
              lineIndex: li,
              vertexIndex: lastIdx,
            },
          });
        }
      }

      // Dogleg endpoint grip.
      if (landingEnabled && doglegEnabled) {
        const endpoint = computeDoglegEndpoint(
          node,
          doglegLength,
          textLocationX,
        );
        if (endpoint) {
          out.push({
            id: `mleader-dogleg-${ri}-${ni}`,
            entityId: entity.id,
            kind: "mleader-dogleg",
            shape: "square",
            world: { x: endpoint.x, y: endpoint.y },
            payload: { kind: "dogleg", rootIndex: ri, nodeIndex: ni },
          });
        }
      }
    }
  }

  // Text content grip — exactly one per multileader.
  out.push({
    id: "mleader-text",
    entityId: entity.id,
    kind: "mleader-text",
    shape: "square",
    world: { x: textLocationX, y: textLocationY },
    payload: { kind: "text" },
  });

  return out;
}

/**
 * GRIPS sysvar mode (AutoCAD GRIPS system variable):
 *   0 — no grips at all (handled upstream in `GripsDomLayer`).
 *   1 — object grips only; block-component (dynamic / MINSERT) grips are
 *       suppressed. The ordinary INSERT insertion grip + every non-block
 *       object grip are still shown.
 *   2 — full: object grips PLUS block-component grips (AutoCAD default).
 */
export type GripsMode = 1 | 2;

/**
 * Dynamic-parameter + MINSERT-grid block-component grips for an INSERT.
 *
 * AutoCAD GRIPS=1 shows object grips (the ordinary insertion grip) but NOT
 * block-component grips; GRIPS=2 adds them — so this returns `[]` unless
 * `gripsMode === 2` (and the block resolves to a definition). Extracted so
 * BOTH grip sources — the legacy string-bag `computeGrips` Block branch AND
 * the S12b typed-first grip source (`typedEntityGrips`, whose cad-core base
 * set is the single insertion grip only, per `cad-core/grips.ts`'s Block
 * scope note) — layer the SAME dynamic-block subsystem on top from one truth
 * (`collectBlockGrips` + `mapBlockGripKind`), never a drifting second copy.
 */
export function blockDynamicGrips(
  entity: EntityPreview,
  blockDefinitions: Record<string, BlockDefinition> | undefined,
  gripsMode: GripsMode,
): Grip[] {
  if (entity.type !== "Block" || gripsMode !== 2) return [];
  const blockName = entity.properties["Name"];
  const blockDef = blockName ? blockDefinitions?.[blockName] : undefined;
  if (!blockDef) return [];
  const out: Grip[] = [];
  const blockGrips: BlockGrip[] = collectBlockGrips(entity, blockDef);
  for (const bg of blockGrips) {
    const { kind, shape } = mapBlockGripKind(bg.kind);
    out.push({
      id: bg.id,
      entityId: entity.id,
      kind,
      shape,
      world: { x: bg.x, y: bg.y },
      payload: bg,
    });
  }
  return out;
}

/**
 * Top-level grip computation. Takes an EntityPreview (and, for INSERT
 * entities, the resolved BlockDefinition map) and returns the full
 * set of grips the overlay must paint for that entity.
 *
 * `gripsMode` is the GRIPS sysvar value (default 2). When `1`, the
 * dynamic-parameter / MINSERT block-component grips are omitted while the
 * insertion grip + all ordinary object grips are kept — AutoCAD GRIPS=1
 * vs GRIPS=2 parity. GRIPS=0 is handled before this function is reached.
 *
 * The function is pure — same input ⇒ same output. Use it from React
 * `useMemo` hooks keyed on the entity property bag + selection.
 */
export function computeGrips(
  entity: EntityPreview,
  blockDefinitions?: Record<string, BlockDefinition>,
  gripsMode: GripsMode = 2,
): Grip[] {
  // Multileader has its own grip pass — see the matching SVG renderer
  // at `renderMultileaderGrips.tsx`.
  if (entity.type === "Multileader") {
    return computeMultileaderGrips(entity);
  }

  const out: Grip[] = [];

  // Block insert: standard insertion grip from calculateGrips,
  // plus dynamic-parameter / MINSERT grips from collectBlockGrips.
  if (entity.type === "Block") {
    // Standard insertion grip first (alignment-shape glyph).
    const standard = calculateGrips(entity);
    for (const g of standard) {
      const { kind, shape } = entityGripKind(
        entity.type,
        g.id,
        g.gripShape,
        g.gripRole,
      );
      out.push({
        id: g.id,
        entityId: entity.id,
        kind,
        shape,
        world: { x: g.x, y: g.y },
        payload: g,
        rotationDeg: g.gripRotationDeg,
      });
    }

    // Dynamic + MINSERT grips — block-component grips (shared with the
    // typed-first grip source, S12b — see `blockDynamicGrips`).
    for (const bg of blockDynamicGrips(entity, blockDefinitions, gripsMode)) {
      out.push(bg);
    }
    return out;
  }

  // Everything else: entity-shape grips from calculateGrips.
  const grips = calculateGrips(entity);
  for (const g of grips) {
    const { kind, shape } = entityGripKind(
      entity.type,
      g.id,
      g.gripShape,
      g.gripRole,
    );
    out.push({
      id: g.id,
      entityId: entity.id,
      kind,
      shape,
      world: { x: g.x, y: g.y },
      payload: g,
      rotationDeg: g.gripRotationDeg,
    });
  }
  return out;
}
